/**
 * Shared user-search hook. Debounced, cached, quota-aware.
 *
 * Both the sidebar's global search and the in-chat invite box use this
 * so the two boxes behave identically — same 3-char minimum, same
 * 500 ms debounce, same 120 s result cache keyed by lowercased query.
 * The cache in particular matters because every `/users/search`
 * hits a KV `list` server-side (class-B; 1K/day on the free plan),
 * and re-typing the same string repeatedly would otherwise eat it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatClient } from './api.js';

export interface SearchableProfile {
  userId: string;
  username?: string;
  displayName?: string;
}

interface CacheEntry {
  at: number;
  results: SearchableProfile[];
}

export interface UseUserSearchResult {
  /** Current free-text query. */
  readonly query: string;
  readonly setQuery: (q: string) => void;
  /** Latest successful results for the current query. */
  readonly results: SearchableProfile[];
  /** Latest error, if any. Cleared on next successful search. */
  readonly error: string | undefined;
  /** Imperative reset — clears query + results. */
  readonly reset: () => void;
}

export function useUserSearch(client: ChatClient, token: string): UseUserSearchResult {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchableProfile[]>([]);
  const [error, setError] = useState<string | undefined>();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());

  const run = useCallback(
    async (q: string) => {
      setError(undefined);
      const trimmed = q.trim().toLowerCase();
      if (trimmed.length < 3) {
        setResults([]);
        return;
      }
      const cached = cacheRef.current.get(trimmed);
      if (cached && Date.now() - cached.at < 120_000) {
        setResults(cached.results);
        return;
      }
      try {
        const r = await client.searchUsers(token, trimmed);
        setResults(r);
        cacheRef.current.set(trimmed, { at: Date.now(), results: r });
      } catch (err) {
        setResults([]);
        setError(err instanceof Error ? err.message : 'search failed');
      }
    },
    [client, token],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void run(query);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, run]);

  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(undefined);
  }, []);

  return { query, setQuery, results, error, reset };
}
